import "./input-group.css";

import {
	Checkbox,
	NumericTextBox,
} from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { Input } from "@tql/ui-components-react/tql/inputs/beta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { TraxInfo } from "~/shared/components/trax-info/trax-info.tsx";
import type { InputFieldConfig } from "~/shared/types/input-group-types";

/**
 * Sentinel id used for the synthetic "clear" entry added to clearable
 * dropdowns. It must not collide with any real option id.
 */
const CLEAR_OPTION_ID = "__clear__";

export type InputGroupValue =
	| string
	| number
	| string[]
	| boolean
	| undefined
	| null
	| Record<string, unknown>;

type InputGroupProps<T = Record<string, unknown>> = {
	readonly fields: InputFieldConfig[];
	item?: T;
	onChange: (field: keyof T, value: InputGroupValue) => void;
	groupId: number;
	onBlurField?: () => void;
	validationErrors?: Set<string>;
};
function FieldLabel({
	field,
	groupId,
}: Readonly<{
	field: InputFieldConfig;
	groupId: number;
}>) {
	const renderLabel = () => {
		if (field.label.includes("<br")) {
			// eslint-disable-next-line react/no-danger -- Safe because label is controlled and only contains <br> for line breaks
			return <span dangerouslySetInnerHTML={{ __html: field.label }} />;
		}
		return field.label;
	};

	return (
		<Label editorId={`input-${field.key}-${groupId}`} id={field.labelId}>
			{field.hasToolTip && field.tooltip ? (
				<>
					{renderLabel()}&nbsp;
					<TraxInfo tooltip={field.tooltip} />
				</>
			) : (
				renderLabel()
			)}
			{field.required ? <span className={"required"}>&nbsp;*</span> : null}
		</Label>
	);
}

function NumericField<T>({
	field,
	groupId,
	onChange,
	onBlurField,
	hasError,
}: Readonly<{
	field: InputFieldConfig;
	groupId: number;
	onChange: (field: keyof T, value: InputGroupValue) => void;
	onBlurField?: () => void;
	hasError?: boolean;
}>) {
	// Round up the value for display
	const displayValue =
		field.value !== null && field.value !== undefined
			? Math.ceil(Number(field.value))
			: null;

	const inputRef = useRef<HTMLInputElement>(null);

	// Use useEffect to add event listeners to the NumericTextBox input
	useEffect(() => {
		const input = inputRef.current;
		if (!input) return;

		// Find the actual input element within the NumericTextBox
		const numericInput = input.querySelector("input");
		if (!numericInput) return;

		const handleKeyDownDirect = (event: globalThis.KeyboardEvent) => {
			const blockedKeys = [
				".",
				",",
				"e",
				"E",
				"+",
				"-",
				"Decimal",
				"NumpadDecimal",
			];
			if (blockedKeys.includes(event.key)) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
				return false;
			}
			return true;
		};

		const handlePasteDirect = (event: globalThis.ClipboardEvent) => {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			const pastedText = event.clipboardData?.getData("text").trim() || "";
			const cleanedText = pastedText.replaceAll(/\D/g, "");

			if (cleanedText && /^\d+$/.test(cleanedText)) {
				numericInput.value = cleanedText;
				const numericValue = Number(cleanedText);
				onChange(field.key as keyof T, numericValue);
			}
		};

		const handleInputDirect = (event: Event) => {
			const target = event.target as HTMLInputElement;
			const value = target.value;
			const hasInvalidCharacters = /[^0-9,]/.test(value);

			if (!hasInvalidCharacters) {
				return;
			}

			const cleanValue = value.replaceAll(/\D/g, "");
			target.value = cleanValue;
			const numericValue = cleanValue === "" ? null : Number(cleanValue);
			onChange(field.key as keyof T, numericValue);
		};

		// Add event listeners to the actual input element
		numericInput.addEventListener("keydown", handleKeyDownDirect, true);
		numericInput.addEventListener("keypress", handleKeyDownDirect, true);
		numericInput.addEventListener("paste", handlePasteDirect, true);
		numericInput.addEventListener("input", handleInputDirect, true);
		numericInput.addEventListener("change", handleInputDirect, true);

		return () => {
			numericInput.removeEventListener("keydown", handleKeyDownDirect, true);
			numericInput.removeEventListener("keypress", handleKeyDownDirect, true);
			numericInput.removeEventListener("paste", handlePasteDirect, true);
			numericInput.removeEventListener("input", handleInputDirect, true);
			numericInput.removeEventListener("change", handleInputDirect, true);
		};
	}, [field.key, onChange]);

	return (
		<div ref={inputRef}>
			<NumericTextBox
				aria-describedby={field.ariaDescribedBy}
				className={field.className || "numeric-quote-details-input"}
				disabled={field.disabled}
				format={"n0"}
				id={`input-${field.key}-${groupId}`}
				name={field.key}
				onBlur={
					field.key === "quantity" && onBlurField
						? () => {
								onBlurField();
							}
						: undefined
				}
				onChange={(e) => {
					const rawValue = e.value;
					const nextValue =
						rawValue == null ? null : Math.ceil(Number(rawValue));
					onChange(field.key as keyof T, nextValue);
				}}
				spinners={false}
				tabIndex={field.tabIndex}
				valid={!hasError}
				value={displayValue}
			/>
		</div>
	);
}

function DropdownField<T>({
	field,
	groupId,
	onChange,
	hasError,
}: Readonly<{
	field: InputFieldConfig;
	groupId: number;
	onChange: (field: keyof T, value: InputGroupValue) => void;
	hasError?: boolean;
}>) {
	const [searchString, setSearchString] = useState("");
	const searchTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				globalThis.clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	const textField = field.textField || "name";

	const data = useMemo(() => {
		const mapped =
			field.data?.map((item) => ({
				...item,
				id: String(item.id),
			})) ?? [];

		// Natural sort ensures "50" < "55" < "500"
		const sorted = [...mapped].sort((a, b) => {
			const valA = String(a[textField as keyof typeof a] ?? "");
			const valB = String(b[textField as keyof typeof b] ?? "");
			return valA.localeCompare(valB, undefined, {
				numeric: true,
				sensitivity: "base",
			});
		});

		// Prepend a selectable blank entry so a set value can be cleared.
		if (field.clearable) {
			const clearLabel = field.clearLabel ?? "Select";
			const clearOption = {
				id: CLEAR_OPTION_ID,
				name: clearLabel,
				code: clearLabel,
			};
			return [clearOption, ...sorted];
		}

		return sorted;
	}, [field.data, field.clearable, field.clearLabel, textField]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			// Ignore if not a single character or has modifiers
			if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) {
				return;
			}

			// Don't interfere with space/enter if they open the dropdown
			if (e.key === " " || e.key === "Enter") {
				return;
			}

			if (searchTimeoutRef.current) {
				globalThis.clearTimeout(searchTimeoutRef.current);
			}

			const currentSearch = searchString + e.key;
			setSearchString(currentSearch);

			searchTimeoutRef.current = globalThis.setTimeout(() => {
				setSearchString("");
			}, 1000) as unknown as number;

			const match = data.find((item) => {
				const val = String(item[textField as keyof typeof item] ?? "");
				return val.toLowerCase().startsWith(currentSearch.toLowerCase());
			});

			if (match) {
				e.preventDefault();
				e.stopPropagation();

				if (field.clearable && match.id === CLEAR_OPTION_ID) {
					onChange(
						field.key as keyof T,
						(field.clearValue as InputGroupValue) ?? "",
					);
					return;
				}

				// We need to pass the original object shape back if the value was an object
				// Find the corresponding item in the original field.data
				const originalItem = field.data?.find(
					(item) => String(item.id) === match.id,
				);

				onChange(
					field.key as keyof T,
					(originalItem as Record<string, unknown>) ??
						(match as Record<string, unknown>),
				);
			}
		},
		[data, field, searchString, textField, onChange],
	);

	const resetSearch = useCallback(() => {
		setSearchString("");
		if (searchTimeoutRef.current) {
			globalThis.clearTimeout(searchTimeoutRef.current);
		}
	}, []);

	const getSelectedId = useCallback((val: unknown): string => {
		if (typeof val === "object" && val !== null && "id" in val) {
			const idVal = (val as { id: unknown }).id;
			return typeof idVal === "string" || typeof idVal === "number"
				? String(idVal)
				: "";
		}
		return typeof val === "string" || typeof val === "number"
			? String(val)
			: "";
	}, []);

	return (
		<div data-testid={field.testId} onKeyDownCapture={handleKeyDown}>
			<DropDownList
				aria-describedby={field.ariaDescribedBy}
				aria-label={field.label}
				aria-labelledby={field.labelId}
				className={"numeric-quote-details-dropdown"}
				data={data}
				dataItemKey={"id"}
				disabled={field.disabled}
				id={`input-${field.key}-${groupId}`}
				name={field.key}
				onBlur={resetSearch}
				onChange={(e: { value: InputGroupValue }) => {
					// Selecting the synthetic blank entry resets the field to its
					// configured clear value instead of the placeholder object.
					if (field.clearable && getSelectedId(e.value) === CLEAR_OPTION_ID) {
						onChange(
							field.key as keyof T,
							(field.clearValue as InputGroupValue) ?? "",
						);
						return;
					}
					// Ensure we pass back whatever type Kendo provides (usually the object from 'data')
					onChange(field.key as keyof T, e.value ?? "");
				}}
				onFocus={resetSearch}
				tabIndex={field.tabIndex}
				textField={textField}
				valid={!hasError}
				value={field.value as InputGroupValue}
			/>
			<select
				aria-hidden={true}
				aria-label={field.label}
				aria-labelledby={field.labelId}
				disabled={field.disabled}
				id={`select-${field.key}-${groupId}`}
				name={field.key}
				onChange={(e) => {
					if (field.clearable && e.target.value === CLEAR_OPTION_ID) {
						onChange(
							field.key as keyof T,
							(field.clearValue as InputGroupValue) ?? "",
						);
						return;
					}
					const val = data.find((d) => d.id === e.target.value);
					const originalItem = field.data?.find(
						(item) => String(item.id) === e.target.value,
					);
					onChange(
						field.key as keyof T,
						(originalItem as Record<string, unknown>) ??
							(val as Record<string, unknown>),
					);
				}}
				style={{
					position: "absolute",
					width: 1,
					height: 1,
					padding: 0,
					margin: "-1px",
					overflow: "hidden",
					clip: "rect(0 0 0 0)",
					border: 0,
				}}
				tabIndex={-1}
				value={getSelectedId(field.value)}
			>
				{data.map((item) => {
					const labelVal = item[textField as keyof typeof item];
					return (
						<option key={item.id} value={item.id}>
							{typeof labelVal === "string" || typeof labelVal === "number"
								? labelVal
								: ""}
						</option>
					);
				})}
			</select>
		</div>
	);
}

function InputFieldComponent<T>({
	field,
	groupId,
	onChange,
	hasError,
}: Readonly<{
	field: InputFieldConfig;
	groupId: number;
	onChange: (field: keyof T, value: InputGroupValue) => void;
	hasError?: boolean;
}>) {
	const [localValue, setLocalValue] = useState<string>(
		(field.value as string) || "",
	);
	const isUserTypingRef = useRef(false);

	// Update local value when field value changes (for external updates)
	useEffect(() => {
		const newValue = (field.value as string) || "";
		// Only update if we're not in the middle of a user typing session
		if (newValue !== localValue && !isUserTypingRef.current) {
			setLocalValue(newValue);
		}
	}, [field.value, localValue]);

	const handleChange = useCallback(
		(e: { value: string }) => {
			let newValue = e.value;

			// If pattern is specified, filter the input
			if (field.pattern) {
				const regex = new RegExp(`[^${field.pattern}]`, "g");
				newValue = newValue.replace(regex, "");
			}

			isUserTypingRef.current = true;

			// Update local state synchronously to prevent cursor jumping
			flushSync(() => {
				setLocalValue(newValue);
			});

			// Defer parent update to next tick to avoid conflicts
			setTimeout(() => {
				onChange(field.key as keyof T, newValue);
				isUserTypingRef.current = false;
			}, 0);
		},
		[field.key, field.pattern, onChange],
	);

	if (field.disabled) {
		return (
			<input
				disabled
				aria-describedby={field.ariaDescribedBy}
				id={`input-${field.key}-${groupId}`}
				name={field.key}
				value={field.value as string | undefined}
			/>
		);
	}
	return (
		<Input
			aria-describedby={field.ariaDescribedBy}
			id={`input-${field.key}-${groupId}`}
			name={field.key}
			onChange={handleChange}
			tabIndex={field.tabIndex}
			valid={!hasError}
			value={localValue}
		/>
	);
}

function CheckboxField<T>({
	field,
	groupId,
	onChange,
}: Readonly<{
	field: InputFieldConfig;
	groupId: number;
	onChange: (field: keyof T, value: InputGroupValue) => void;
}>) {
	return (
		<Checkbox
			aria-describedby={field.ariaDescribedBy}
			checked={!!field.value}
			disabled={field.disabled}
			id={`input-${field.key}-${groupId}`}
			name={field.key}
			onChange={(e) => onChange(field.key as keyof T, e.value)}
			tabIndex={field.tabIndex}
		/>
	);
}

function formatValue(value: unknown): string {
	if (typeof value === "number") {
		return value.toLocaleString();
	}
	return String(value);
}

function DisplayField({ field }: Readonly<{ field: InputFieldConfig }>) {
	const displayValue = formatValue(field.value);
	const className = [
		"display-field",
		displayValue === "—" ? "display-field-empty" : undefined,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={className}
			title={field.hasToolTip && field.tooltip ? field.tooltip : undefined}
		>
			{displayValue}
		</div>
	);
}

export const InputFieldsGroup = <T = Record<string, unknown>,>({
	fields,
	onChange,
	groupId,
	onBlurField,
	validationErrors,
}: InputGroupProps<T>) => {
	const renderField = (field: InputFieldConfig) => {
		const hasError = validationErrors?.has(field.key) ?? false;

		return (
			<div
				key={`${field.key}-${groupId}`}
				className={"input-field-group input-field-group-" + field.key}
				data-field-key={field.key}
			>
				<div
					className={"input-field-group-inner"}
					data-testid={`input-field-group-${field.key}-${groupId}`}
					style={{ ...field.wrapperStyle, position: "relative" }}
				>
					<FieldLabel field={field} groupId={groupId} />
					<div className={"input-field-control"}>
						{field.type === "numeric" && (
							<NumericField<T>
								field={field}
								groupId={groupId}
								hasError={hasError}
								onBlurField={onBlurField}
								onChange={onChange}
							/>
						)}
						{field.type === "dropdown" && (
							<DropdownField<T>
								field={field}
								groupId={groupId}
								hasError={hasError}
								onChange={onChange}
							/>
						)}
						{field.type === "input" && (
							<InputFieldComponent<T>
								field={field}
								groupId={groupId}
								hasError={hasError}
								onChange={onChange}
							/>
						)}
						{field.type === "checkbox" && (
							<CheckboxField<T>
								field={field}
								groupId={groupId}
								onChange={onChange}
							/>
						)}
						{field.type === "display" && <DisplayField field={field} />}
					</div>
					{hasError ? <Error>Required</Error> : null}
					{field.hint ? (
						<span className={"field-hint"}>{field.hint}</span>
					) : null}
				</div>
				{field.after ? (
					<div key={`${field.key}-${groupId}-after`}>{field.after}</div>
				) : null}
			</div>
		);
	};

	return (
		<div className={"input-group"}>
			{fields.map((field) => {
				if (field.show === false) {
					return null;
				}

				return renderField(field);
			})}
		</div>
	);
};
